import { Card, Typography, Theme, Divider } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { NextSeo } from 'next-seo'
import React from 'react'
import { GetServerSideProps } from 'next'
import dynamic from 'next/dynamic'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'

import Background from '../src/components/Subnav/Background'

const SeoText = dynamic(() => import('../src/components/SeoText/HowToChooseWallet'))
const Description = dynamic(() => import('../src/components/Subnav/Description'))
const WalletCard = dynamic(() => import('../src/components/Card'))
const HardwareWalletCard = dynamic(() => import('../src/components/Card/Hardware'))
import Nav from '../src/components/Nav'
import Footer from '../src/components/Footer'

import { getWallets } from '../src/services/wallets'
import { urls, tabs } from '../src/utils/config'
import { Wallet as IWallet } from '../types/wallet'
import Link from 'next/link'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '950px',
    margin: 'auto',
  },
  container: {
    marginTop: '100px',
    [theme.breakpoints.down('md')]: {
      padding: '20px',
    },
  },
  subContainer: {
    margin: '50px 0',
  },
  typesContainer: {},
  yellowTitleContainer: {
    marginBottom: '20px',
    left: '-10px',
    position: 'relative',
    width: 'fit-content',
    '& h5': {
      fontSize: '16px',
      color: theme.palette.primary.main,
      position: 'relative',
    },
  },
  yellowTitleBackground: {
    position: 'absolute',
    top: '10px',
    left: '-5px',
    width: '100%',
    backgroundColor: theme.palette.secondary.main,
    height: '18px',
    zIndex: -1,
  },
  yellowTitle: {
    color: theme.palette.primary.main,
    fontSize: '20px',
    marginBottom: '50px',
  },
  prosAndCons: {
    display: 'flex',
    flexDirection: 'row',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    margin: '20px 0',
    minHeight: '50px',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
  },
  pros: {
    width: '50%',
    [theme.breakpoints.down('md')]: {
      marginRight: '10px',
    },
  },
  cons: {
    width: '50%',
    [theme.breakpoints.down('md')]: {
      marginLeft: '10px',
    },
  },
  card: {
    padding: '20px',
    border: `2px solid ${theme.palette.primary.main}`,
    width: '350px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginRight: '80px',
    marginBottom: '20px',
    '&:hover': {
      cursor: 'pointer',
      opacity: '0.8',
    },
    [theme.breakpoints.down('md')]: {
      marginRight: '0',
    },
  },
  bigCard: {
    height: '280px',
  },
  smallCard: {
    height: '250px',
  },
  smallIcon: {
    height: 'auto',
    minWidth: '20px',
    [theme.breakpoints.down('md')]: {
      marginBottom: '20px',
    },
  },
  bigIcon: {
    height: '35px',
    width: 'auto',
    margin: '20px 0',
    objectFit: 'contain',
  },
  divider: {
    backgroundColor: theme.palette.secondary.main,
    height: '3px',
    margin: '20px 0',
    width: '100%',
  },
  purpleTitle: {
    fontWeight: 'bold',
    fontSize: '20px',
    lineHeight: '22px',
    color: theme.palette.primary.main,
  },
  title: {
    fontSize: '20px',
    color: theme.palette.text.secondary,
    margin: '40px 0',
  },
  mainTitle: {
    fontSize: '22px',
    color: theme.palette.primary.main,
    margin: '40px 0',
  },
  link: {
    margin: '20px 0',
    '& h6': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
      fontSize: '20px',
      fontWeight: 'normal',
      '&:hover': {
        cursor: 'pointer',
        opacity: '0.8',
      },
    },
  },
  proCon: {
    marginLeft: '20px',
  },
}))

interface Props {
  wallets: IWallet[]
}

function HowToChooseWalletContainer({ wallets }: Props): JSX.Element {
  const classes = useStyles({})
  const custodialWallets = wallets
    .filter((obj) => obj.category.code === 'custodial-wallets')
    .slice(0, 2)
  const multiCoinWallets = wallets
    .filter(
      (obj) =>
        obj.category.code === 'non-custodial-wallets' &&
        obj.cryptocurrencies.length >= 2 &&
        obj.cryptocurrencies.find((obj2) => obj2.code === 'BTC') &&
        obj.cryptocurrencies.find((obj2) => obj2.code === 'ETH'),
    )
    .slice(0, 2)
  const bitcoinWallets = wallets
    .filter((obj) => obj.pure_currency && obj.cryptocurrencies.find((obj2) => obj2.code === 'BTC'))
    .slice(0, 2)
  const ethereumWallets = wallets
    .filter((obj) => obj.pure_currency && obj.cryptocurrencies.find((obj2) => obj2.code === 'ETH'))
    .slice(0, 2)
  const hardwareWallets = wallets
    .filter((obj) => obj.category.code === 'hardware-wallets')
    .slice(0, 2)
  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.howToChooseCryptoWallet,
        },
      })}
    >
      {(): JSX.Element => (
        <>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`Cryptocurrency Wallet Guide | All you need to know about Crypto Wallets to get started`}
            description={`There are a range of cryptocurrency wallets to choose from. In this comprehensive crypto wallet guide we teach you how to pick one that suits your needs.`}
            openGraph={{
              url: `https://cryptotesters.com${urls.howToChooseCryptoWallet}`,
              title: `Cryptocurrency Wallet Guide | All you need to know about Crypto Wallets to get started`,
              description: `There are a range of cryptocurrency wallets to choose from. In this comprehensive crypto wallet guide we teach you how to pick one that suits your needs.`,
              images: [
                {
                  url: 'https://cryptotesters.com/sharing.png',
                  width: 1200,
                  height: 630,
                  alt: `Cryptotesters.com`,
                },
                {
                  url: 'https://cryptotesters.com/sharable_small.png',
                  width: 600,
                  height: 400,
                  alt: `Cryptotesters.com Logo`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <Nav />
          <div className={classes.root}>
            <Background />
            <Description selectedTab={tabs.howToChooseCryptoWallet} />
            <div className={classes.container}>
              <div className={classes.typesContainer}>
                <div className={classes.yellowTitleContainer}>
                  <div className={classes.yellowTitleBackground} />
                  <Typography variant="subtitle1" className={classes.yellowTitle}>
                    Custodial vs. Non-Custodial Wallets
                  </Typography>
                </div>
                <div className={classes.row}>
                  <Link href={urls.custodialWallets}>
                    <Card className={`${classes.card} ${classes.bigCard}`}>
                      <Typography variant="subtitle1" className={classes.purpleTitle}>
                        Custodial
                      </Typography>
                      <Divider className={classes.divider} />
                      <img
                        width={40}
                        height={40}
                        src={`/svg/usb-purple.svg`}
                        className={classes.bigIcon}
                      />
                      <Typography variant="body1">
                        Custodial wallets are like banks. The provider controls your assets and
                        saves them for you.
                      </Typography>
                    </Card>
                  </Link>
                  <Link href={urls.nonCustodialWallets}>
                    <Card className={`${classes.card} ${classes.bigCard}`}>
                      <Typography variant="subtitle1" className={classes.purpleTitle}>
                        Non-Custodial
                      </Typography>
                      <Divider className={classes.divider} />
                      <img
                        width={40}
                        height={40}
                        src={`/svg/key.svg`}
                        className={classes.bigIcon}
                      />
                      <Typography variant="body1">
                        Non-custodial wallets allow you to keep complete ownership of your funds.
                        Your funds are stored on the Blockchain.
                      </Typography>
                    </Card>
                  </Link>
                </div>
              </div>

              <div className={classes.subContainer}>
                <Typography variant="h5" className={classes.mainTitle}>
                  Pros and Cons of a Custodial Wallet
                </Typography>
                <div className={classes.prosAndCons}>
                  <div className={classes.pros}>
                    <div className={classes.row}>
                      <img
                        width={15}
                        height={15}
                        src="/svg/plus.svg"
                        className={classes.smallIcon}
                        alt="plus icon"
                      />
                      <Typography variant="body1" className={classes.proCon}>
                        No risk of losing private key/access to your funds
                      </Typography>
                    </div>
                    <div className={classes.row}>
                      <img
                        width={15}
                        height={15}
                        src="/svg/plus.svg"
                        className={classes.smallIcon}
                        alt="plus icon"
                      />
                      <Typography variant="body1" className={classes.proCon}>
                        Can hold fiat currencies ($,€) in addition to crypto
                      </Typography>
                    </div>
                    <div className={classes.row}>
                      <img
                        width={15}
                        height={15}
                        src="/svg/plus.svg"
                        className={classes.smallIcon}
                        alt="plus icon"
                      />
                      <Typography variant="body1" className={classes.proCon}>
                        Similar user experience to traditional apps
                      </Typography>
                    </div>
                  </div>
                  <div className={classes.cons}>
                    <div className={classes.row}>
                      <img
                        width={15}
                        height={15}
                        src="/svg/minus.svg"
                        className={classes.smallIcon}
                        alt="minus icon"
                      />
                      <Typography variant="body1" className={classes.proCon}>
                        No{' '}
                        <b>
                          <u>direct</u>
                        </b>{' '}
                        ownership over funds
                      </Typography>
                    </div>
                    <div className={classes.row}>
                      <img
                        width={15}
                        height={15}
                        src="/svg/minus.svg"
                        className={classes.smallIcon}
                        alt="minus icon"
                      />
                      <Typography variant="body1" className={classes.proCon}>
                        Users need to identify themselves (KYC etc.)
                      </Typography>
                    </div>
                    <div className={classes.row}>
                      <img
                        width={15}
                        height={15}
                        src="/svg/minus.svg"
                        className={classes.smallIcon}
                        alt="minus icon"
                      />
                      <Typography variant="body1" className={classes.proCon}>
                        Geographically limited, not available worldwide
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>

              <div className={classes.subContainer}>
                <Typography variant="h6" className={classes.mainTitle}>
                  Custodial Crypto Wallets
                </Typography>
                <Typography variant="body1">
                  Custodial wallets are a great place to start when you’re new to crypto, as they
                  are fairly similar to modern banking apps, allow users to seamlessly buy & send
                  crypto as well as storing multiple cryptocurrencies in the same app.
                </Typography>
                {custodialWallets.map((obj) => (
                  <WalletCard hideFeature key={obj._id} custodial={true} wallet={obj} />
                ))}
                <div className={classes.link}>
                  <Link href={urls.custodialWallets}>
                    <Typography variant="subtitle1">
                      Compare all custodial wallets &gt;&gt;
                    </Typography>
                  </Link>
                </div>
              </div>

              <div className={classes.subContainer}>
                <Typography variant="h6" className={classes.mainTitle}>
                  Non-Custodial Crypto Wallets
                </Typography>
                <Typography variant="body1">
                  Non-custodial wallets are the real deal when you want to get seriously into
                  crypto. Non-custodial wallets allow you to directly interact with the Blockchain,
                  where your funds are stored. The wallet provider merely provides an interface for
                  you to communicate with the Blockchain network. Crucially, no one but you can
                  access your wallets funds. Not even the government.
                </Typography>
              </div>

              <div className={classes.subContainer}>
                <Typography variant="h6" className={classes.title}>
                  Types of Non-Custodial Crypto Wallets
                </Typography>
                <div className={classes.row}>
                  <Link href={urls.nonCustodialWallets}>
                    <Card className={`${classes.card} ${classes.smallCard}`}>
                      <Typography variant="subtitle1" className={classes.purpleTitle}>
                        Multicoin wallets
                      </Typography>
                      <Divider className={classes.divider} />
                      <Typography variant="body1">
                        They allow you to store several cryptocurrencies in one wallet app. For
                        example, bitcoin and ether.
                      </Typography>
                    </Card>
                  </Link>{' '}
                  <Link href={urls.bitcoinWallets}>
                    <Card className={`${classes.card} ${classes.smallCard}`}>
                      <Typography variant="subtitle1" className={classes.purpleTitle}>
                        Bitcoin wallets
                      </Typography>
                      <Divider className={classes.divider} />
                      <Typography variant="body1">
                        Bitcoin wallets allow you to store only bitcoin, but offer special, bitcoin
                        specific features.
                      </Typography>
                    </Card>
                  </Link>{' '}
                  <Link href={urls.ethereumWallets}>
                    <Card className={`${classes.card} ${classes.smallCard}`}>
                      <Typography variant="subtitle1" className={classes.purpleTitle}>
                        Ethereum wallets
                      </Typography>
                      <Divider className={classes.divider} />
                      <Typography variant="body1">
                        Ethereum wallets work only on Ethereum but allow you to use decentralized
                        Ethereum applications a.k.a “DeFi”
                      </Typography>
                    </Card>
                  </Link>{' '}
                  <Link href={urls.hardwareWallets}>
                    <Card className={`${classes.card} ${classes.smallCard}`}>
                      <Typography variant="subtitle1" className={classes.purpleTitle}>
                        Hardware wallets
                      </Typography>
                      <Divider className={classes.divider} />
                      <Typography variant="body1">
                        Hardware wallets are similar to Multicoin wallets. They allow you to store
                        multiple cryptocurrencies but store the private keys on a hardware device.
                      </Typography>
                    </Card>
                  </Link>
                </div>
              </div>

              <div className={classes.subContainer}>
                <Typography variant="h5" className={classes.title}>
                  Best Multicoin wallets
                </Typography>
                {multiCoinWallets.map((obj) => (
                  <WalletCard hideFeature key={obj._id} custodial={false} wallet={obj} />
                ))}
                <div className={classes.link}>
                  <Link href={urls.nonCustodialWallets}>
                    <Typography variant="subtitle1">
                      Compare all multicoin wallets &gt;&gt;
                    </Typography>
                  </Link>
                </div>
              </div>

              <div className={classes.subContainer}>
                <Typography variant="h5" className={classes.title}>
                  Best Bitcoin wallets
                </Typography>
                {bitcoinWallets.map((obj) => (
                  <WalletCard hideFeature key={obj._id} custodial={false} wallet={obj} />
                ))}
                <div className={classes.link}>
                  <Link href={urls.bitcoinWallets}>
                    <Typography variant="subtitle1">
                      Compare all bitcoin wallets &gt;&gt;
                    </Typography>
                  </Link>
                </div>
              </div>

              <div className={classes.subContainer}>
                <Typography variant="h5" className={classes.title}>
                  Best Ethereum wallets
                </Typography>
                {ethereumWallets.map((obj) => (
                  <WalletCard hideFeature key={obj._id} custodial={false} wallet={obj} />
                ))}
                <div className={classes.link}>
                  <Link href={urls.ethereumWallets}>
                    <Typography variant="subtitle1">
                      Compare all ethereum wallets &gt;&gt;
                    </Typography>
                  </Link>
                </div>
              </div>

              <div className={classes.subContainer}>
                <Typography variant="h5" className={classes.title}>
                  Best Hardware wallets
                </Typography>
                {hardwareWallets.map((obj) => (
                  <HardwareWalletCard hideFeature wallet={obj} key={obj._id} />
                ))}
                <div className={classes.link}>
                  <Link href={urls.hardwareWallets}>
                    <Typography variant="subtitle1">
                      Compare all hardware wallets &gt;&gt;
                    </Typography>
                  </Link>
                </div>
              </div>
            </div>
            <SeoText />
          </div>
          <Footer />
        </>
      )}
    </Amplitude>
  )
}

export const getServerSideProps: GetServerSideProps = async () => {
  let wallets: IWallet[] = []

  try {
    wallets = await getWallets()
  } catch (e) {
    console.error(e)
  }

  return {
    props: {
      wallets,
    },
  }
}

export default HowToChooseWalletContainer
